// src/config/flags.js
//
// Build-time feature flags. CRA inlines REACT_APP_* at build time, so these
// are compile-time constants — dead branches tree-shake out and there is no
// runtime cost or flash of the wrong UI.
//
// ── MARKETS_ENABLED ─────────────────────────────────────────────────────
// The XPass bonding curve is being retired in favour of Founding Cards
// (PLAN.md P8). This flag is the single switch that hides every trading,
// pricing and PNL surface at once.
//
// It defaults to FALSE: the curve is RETIRED (PLAN.md 8.6 phase M). The
// default lives here in code rather than in a Vercel env var, so a fresh
// project, a preview deployment or a restored snapshot comes up post-curve
// instead of quietly resurrecting the bonding curve. The escape hatch is
// an explicit opt-in, not the resting state:
//
//     REACT_APP_MARKETS_ENABLED=true     # resurrect the curve surfaces
//
// MUST be kept in step with the server-side `MARKETS_ENABLED` read in
// supabase/functions/_shared/chatGate.ts. They gate different halves of the
// same decision — the client hides the surfaces, the server stops honouring
// curve holdings — and a mismatch is confusing rather than dangerous: the
// server is always the authority on access.
//
// What FALSE means, concretely:
//   · Discover loses the "Wins" tab (a leaderboard of profitable exits has
//     no analogue when the asset has one fixed price)
//   · The creator Market tab loses the price chart, trade tape and token
//     stats, keeping the PassBox and the holder list
//   · Profile loses portfolio value, unrealized PNL and the closes terminal
//   · Public profiles lose the PNL/win-rate metric tiles
//   · Studio loses the agent trading-rules editor
//
// What it does NOT do: hide founding cards. Cards are gated per creator by
// `creators.card_series_id` (the drop being live), not by this flag — the
// two are independent, and both can be on at once during the migration.
// P8 flipped the default: the curve is retired, so post-curve surfaces
// (My Passes, card ticker, PassStats) are the product. Setting
// REACT_APP_MARKETS_ENABLED=true resurrects the curve-era surfaces —
// an escape hatch, not a mode we ship.
export const MARKETS_ENABLED = process.env.REACT_APP_MARKETS_ENABLED === 'true';

// ── PAYMENTS_ENABLED ────────────────────────────────────────────────────
// Direct-payment purchases (message unlock, gift, Chat Pass, paid
// collection). Defaults to FALSE: the server refuses them unless its own
// PAYMENTS_ENABLED=true (payments_unavailable), and this flag keeps the
// UI from opening a wallet for a payment the server would refuse. Missing
// env = off.
export const PAYMENTS_ENABLED = process.env.REACT_APP_PAYMENTS_ENABLED === 'true';
export const PAYMENTS_UNAVAILABLE_MESSAGE = 'Payments are not available yet.';
// Rooms: ETH tickets, requests and replays are off until they move to the credit balance.
export const PAID_TICKETS_UNAVAILABLE = 'Paid tickets are not available yet.';
export const PAID_REQUESTS_UNAVAILABLE = 'Paid requests are not available yet.';

/** Convenience for the common "show a trading/PNL surface?" test, so call
 *  sites read as intent rather than as a negated env lookup. */
export const showTradingSurfaces = () => MARKETS_ENABLED;

// ── REELS_ENABLED ───────────────────────────────────────────────────────
// The vertical reels feed (PLAN.md P7.1). Defaults to FALSE — the opposite
// of MARKETS_ENABLED, and deliberately so: reels depend on a Bunny Stream
// library that is not provisioned yet, so an unconfigured build must not
// show creators an upload box that can only fail. Turn it on with:
//
//     REACT_APP_REELS_ENABLED=true
//
// once BUNNY_STREAM_LIBRARY_ID / BUNNY_STREAM_API_KEY are set as Supabase
// secrets. The server is the real gate either way: `create_draft` answers
// 503 provider_not_configured when the library is missing, so flipping this
// flag early degrades to a clear message rather than a broken upload.
export const REELS_ENABLED = process.env.REACT_APP_REELS_ENABLED === 'true';

// ── REPLAYS_ENABLED ─────────────────────────────────────────────────────
// The Rooms storefront and event replays (PLAN.md P7.5). Defaults to FALSE,
// for the same reason REELS_ENABLED does: the things it renders do not exist
// yet. The `rooms` replay/preview columns (supabase/schema/replays.sql) are
// written but NOT APPLIED, recording (LiveKit Egress) is unbuilt, and the
// `replays` edge function that resolves access has not shipped. Turn it on
// with:
//
//     REACT_APP_REPLAYS_ENABLED=true
//
// once the schema is applied and the function is deployed.
//
// The flag hides a SURFACE, it is not an access control. Entitlement is
// decided server-side by resolveReplayAccess() in
// supabase/functions/_shared/replayAccess.ts, which fails CLOSED — so a
// build with the flag on but the backend missing degrades to "no replays"
// and a retry affordance, never to a free recording.
//
// Declared as its own `const` before it is named in the `flags` object
// below. A previous edit listed a key there without declaring it, which
// is a ReferenceError at module scope and took the whole bundle down on
// first import — not a dead branch, a crash.
export const REPLAYS_ENABLED = process.env.REACT_APP_REPLAYS_ENABLED === 'true';

const flags = { MARKETS_ENABLED, REELS_ENABLED, REPLAYS_ENABLED };
export default flags;
